import { AudioCapture } from "../core/AudioCapture";
import { AudioClip } from "../core/types";
import { resampleTo16kMono } from "./resample";

const WORKLET_URL = `${process.env.PUBLIC_URL}/voice/recorder-worklet.js`;

/**
 * Containers the /Voice/Transcribe endpoint accepts, best first. Opus at a
 * low bitrate is transparent for speech and roughly a tenth the size of the
 * equivalent WAV, which is most of the upload latency. Safari only offers
 * audio/mp4 — that is the m4a container, which the endpoint also accepts.
 */
const PREFERRED_MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

const OPUS_BITS_PER_SECOND = 24000;

function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? null;
}

/**
 * getUserMedia + AudioWorklet implementation of AudioCapture, for web.
 *
 * One microphone stream feeds two consumers: the AudioWorklet, whose frames
 * drive the level meter and the offline VAD gate, and a MediaRecorder, which
 * produces the compressed clip that gets uploaded. They share a stream on
 * purpose — acquiring the device twice is a known source of stalls.
 */
export class WebAudioCapture implements AudioCapture {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private recorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recorderMimeType = "";
  /** Resolves when the recorder has flushed its last chunk. */
  private recorderStopped: Promise<void> | null = null;
  private chunks: Float32Array[] = [];
  private cancelled = false;

  async requestPermission(): Promise<"granted" | "denied" | "unavailable"> {
    if (!navigator.mediaDevices?.getUserMedia || typeof AudioWorkletNode === "undefined") {
      return "unavailable";
    }
    try {
      // Probing stream — released immediately; start() opens the real one.
      // Some browsers only surface the permission prompt via a real getUserMedia call.
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      probe.getTracks().forEach((t) => t.stop());
      return "granted";
    } catch (err) {
      if (err instanceof DOMException && err.name === "NotAllowedError") return "denied";
      if (err instanceof DOMException && err.name === "NotFoundError") return "unavailable";
      return "denied";
    }
  }

  async start(onLevel?: (rms: number) => void): Promise<void> {
    this.chunks = [];
    this.recordedChunks = [];
    this.recorderMimeType = "";
    this.recorderStopped = null;
    this.cancelled = false;

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    this.audioContext = new AudioContext();
    // start() is reached after an await, so the click's user activation may
    // already have expired and the context can be created suspended. A
    // suspended context never runs the worklet's process(), which would leave
    // the level meter dead and every clip empty.
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume().catch(() => {});
    }
    await this.audioContext.audioWorklet.addModule(WORKLET_URL);

    const source = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, "recorder-processor");

    this.workletNode.port.onmessage = (event: MessageEvent<Float32Array>) => {
      const frame = event.data;
      this.chunks.push(frame);
      if (onLevel) onLevel(rms(frame));
    };

    source.connect(this.workletNode);
    // Deliberately not connected to destination — we don't want to hear the mic.

    this.startRecorder(this.mediaStream);
  }

  /**
   * Compressed capture, alongside the worklet. Best-effort: if MediaRecorder
   * is missing, offers no accepted container, or throws, stop() still returns
   * a usable clip and the upload path encodes WAV from pcm16k instead.
   */
  private startRecorder(stream: MediaStream): void {
    const mimeType = pickMimeType();
    if (!mimeType) return;

    try {
      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: OPUS_BITS_PER_SECOND,
      });
      this.recorderStopped = new Promise<void>((resolve) => {
        recorder.onstop = () => resolve();
        // A recorder error leaves no usable blob, but must not hang stop().
        recorder.onerror = () => resolve();
      });
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) this.recordedChunks.push(event.data);
      };
      // No timeslice: one dataavailable at stop, which avoids stitching
      // fragments into a container that then has no valid header.
      recorder.start();
      this.recorder = recorder;
      this.recorderMimeType = mimeType;
    } catch {
      this.recorder = null;
      this.recorderStopped = null;
    }
  }

  async stop(): Promise<AudioClip> {
    const hardwareRate = this.audioContext?.sampleRate ?? 48000;
    // Flush the recorder BEFORE teardown: stopping the tracks first can drop
    // the final chunk, and the container header with it.
    const encoded = await this.finishRecording();
    await this.teardown();

    if (this.cancelled) {
      return { pcm16k: new Float32Array(0), durationMs: 0 };
    }

    const raw = concatenate(this.chunks);
    const pcm16k = resampleTo16kMono(raw, hardwareRate);
    return { pcm16k, durationMs: (pcm16k.length / 16000) * 1000, encoded };
  }

  private async finishRecording(): Promise<{ blob: Blob; mimeType: string } | undefined> {
    const recorder = this.recorder;
    if (!recorder) return undefined;
    this.recorder = null;

    if (recorder.state !== "inactive") {
      recorder.stop();
      await this.recorderStopped;
    }
    this.recorderStopped = null;

    if (this.cancelled || this.recordedChunks.length === 0) return undefined;
    const blob = new Blob(this.recordedChunks, { type: this.recorderMimeType });
    this.recordedChunks = [];
    return { blob, mimeType: this.recorderMimeType };
  }

  cancel(): void {
    this.cancelled = true;
    this.recordedChunks = [];
    if (this.recorder && this.recorder.state !== "inactive") {
      try {
        this.recorder.stop();
      } catch {
        // already stopping — nothing to unwind.
      }
    }
    this.recorder = null;
    this.recorderStopped = null;
    void this.teardown();
  }

  dispose(): void {
    this.cancel();
  }

  private async teardown() {
    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
    if (this.audioContext) {
      await this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }
}

function rms(frame: Float32Array): number {
  let sumSquares = 0;
  for (let i = 0; i < frame.length; i++) sumSquares += frame[i] * frame[i];
  return Math.sqrt(sumSquares / frame.length);
}

function concatenate(chunks: Float32Array[]): Float32Array {
  const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}
